/**
 * Hive Flows endpoints — visual workflow builder (Jun 2026).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/hive_flows.py``
 *   - ``ando-agent/src/schemas/hive_flows.py``
 *   - ``ando-agent/src/services/hive_flows/node_registry.py`` (the palette)
 *
 * Every wire shape mirrors the BE Pydantic model. The node *catalog* is NEVER
 * re-declared here — the FE fetches it via ``getFlowPalette`` so the builder
 * stays in lockstep with the BE registry (single source of truth).
 */

import { apiClient } from "../ApiClient.js";

// ── Graph primitives ─────────────────────────────────────────────────────────
export interface FlowNode {
  id: string;
  node_type: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
}

export interface FlowEdge {
  source: string;
  target: string;
  condition?: string | null;
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
  /** Optional declarative run-surface (L1 "banco"). Read defensively by the FE
   * ``deriveSurface``; absent → the generic review. Passthrough dict BE-side. */
  surface?: Record<string, unknown> | null;
  /** Declarative inputs for manual runs (select/date/number/text). */
  run_form?: Record<string, unknown> | null;
}

export interface ArticleGroupCatalog {
  groups: Array<{ code: string; article_count: number }>;
  total_groups: number;
  ungrouped_articles: number;
  source: string;
  complete: boolean;
}

export async function getFlowArticleGroups(flowId: string): Promise<ArticleGroupCatalog> {
  return apiClient.get<ArticleGroupCatalog>(`/hive-flows/${encodeURIComponent(flowId)}/article-groups`);
}

// ── Flow CRUD ────────────────────────────────────────────────────────────────
export type FlowScheduleType = "manual" | "cron" | "interval" | "once" | "webhook" | "reactive";
export type FlowStatus = "draft" | "active" | "paused" | "archived";

export interface Flow {
  flow_id: string;
  name: string;
  description?: string | null;
  graph: FlowGraph;
  schedule_type: FlowScheduleType;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  run_at?: string | null;
  timezone: string;
  source_config: Record<string, unknown>;
  requires_approval: boolean;
  is_active: boolean;
  status: FlowStatus;
  last_run_at?: string | null;
  next_run_at?: string | null;
  total_runs: number;
  version: number;
  is_template: boolean;
  /** Phase 3 per-project scope (June 9, 2026). NULL = workspace-wide. */
  project_id?: string | null;
  /**
   * Team sharing tier (issue #936): ``private`` (only me) or ``org`` (team
   * catalog, colleagues fork their own copy). Unlike Skills there is no
   * ``org_auto``: a flow's node config is tied to its owner's connected
   * accounts, so using a shared one always requires an explicit fork +
   * review, never in-place activation. Defaults to ``private``.
   */
  visibility?: FlowVisibility;
  /** Who shared it + when (provenance). ``null`` when ``visibility`` is ``private``. */
  shared_by?: string | null;
  shared_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Team sharing tier of a flow. See ``Flow.visibility``. ``project``
 * (issue #1103) is visible only to members of the flow's own
 * ``project_id`` — a lighter-weight alternative to ``org`` for teams
 * that don't want to share with the whole tenant.
 */
export type FlowVisibility = "private" | "org" | "project";

/**
 * A flow shown in the team catalog: a colleague's, never the caller's own.
 * The catalog is unified (issue #1103) — org-wide shares AND project-scoped
 * shares from every project the caller belongs to, in one list.
 */
export interface TeamFlow extends Flow {
  /** Display name of who shared the flow. */
  shared_by_name?: string | null;
  /** Set only when ``visibility === "project"`` — the project's name, for
   * the "Progetto: <name>" badge (vs. "Team" for an org-wide share). */
  project_name?: string | null;
}

export interface TeamFlowsResponse {
  flows: TeamFlow[];
}

/**
 * A node in a freshly-forked flow whose type touches a connected account
 * (email, Drive, database, …). It still points at the ORIGINAL owner's
 * account and needs review before the new owner activates the flow.
 */
export interface ReconciliationNode {
  node_id: string;
  label: string;
  connector: string;
}

export interface DuplicateFlowResult extends Flow {
  reconciliation_nodes: ReconciliationNode[];
}

export interface FlowListResponse {
  flows: Flow[];
  total: number;
}

export interface FlowCreatePayload {
  name: string;
  description?: string | null;
  graph?: FlowGraph;
  schedule_type?: FlowScheduleType;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  timezone?: string;
  source_config?: Record<string, unknown>;
  requires_approval?: boolean;
  template_id?: string | null;
  /** Phase 3 per-project scope (June 9, 2026). Pre-populated by the
   * Detail-page "+ Nuovo flow" CTA so the flow is born inside the
   * project. NULL/omitted = workspace-wide flow. */
  project_id?: string | null;
}

export interface FlowUpdatePayload {
  name?: string;
  description?: string | null;
  graph?: FlowGraph;
  schedule_type?: FlowScheduleType;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  timezone?: string;
  source_config?: Record<string, unknown>;
  requires_approval?: boolean;
  is_active?: boolean;
  status?: FlowStatus;
  /** Re-pin to another project, or send ``null`` to detach. */
  project_id?: string | null;
}

// ── Runs ─────────────────────────────────────────────────────────────────────
export interface FlowRunRequest {
  dry_run?: boolean;
  trigger_data?: Record<string, unknown>;
}

export interface FlowNodeRun {
  node_id: string;
  node_type: string;
  status: "pending" | "running" | "success" | "failed" | "skipped";
  output: Record<string, unknown>;
  error_message?: string | null;
  duration_ms?: number | null;
}

export interface FlowRun {
  run_id: string;
  flow_id: string;
  status: string;
  trigger_type: string;
  is_dry_run: boolean;
  started_at?: string | null;
  completed_at?: string | null;
  duration_ms?: number | null;
  result_summary?: string | null;
  error_message?: string | null;
  total_cost_usd: number;
  node_runs: FlowNodeRun[];
}

/**
 * A run in the history list — the run envelope WITHOUT the heavy per-node
 * projection (``node_runs`` / ``context``). BE: ``GET /hive-flows/{flow_id}/runs``.
 * ``created_at`` lets the rail sort/label runs that never started (queued).
 */
export interface FlowRunSummary {
  run_id: string;
  flow_id: string;
  status: string;
  trigger_type: string;
  is_dry_run: boolean;
  started_at?: string | null;
  completed_at?: string | null;
  duration_ms?: number | null;
  result_summary?: string | null;
  total_cost_usd: number;
  created_at?: string | null;
  /** Seed trigger payload (e.g. the uploaded document's ``filename``) — titles
   * a history row by WHAT it processed rather than the status message. */
  trigger_data?: Record<string, unknown> | null;
  support_case?: SupportCaseState | null;
  support_decision?: { result?: boolean } | null;
}

export interface FlowRunListResponse {
  runs: FlowRunSummary[];
}

/**
 * Full projection of a single run — for POLLING a long AI pipeline and
 * RE-HYDRATING the operator run/review surface after a page reload. BE:
 * ``GET /hive-flows/runs/{run_id}`` → ``FlowRunDetailResponse`` (extends the
 * run-result envelope the ``/run`` + ``/run-upload`` endpoints already return).
 *
 * DRY: inherits the whole run envelope + per-node outputs (``node_runs``) from
 * {@link FlowRun}; adds the seed ``trigger_data``, the accumulated ``context``
 * bag (named node outputs) and the HITL approval audit fields.
 */
export interface FlowRunDetailResponse extends FlowRun {
  /**
   * The seed passed to the run — e.g. ``{ content_item_id, filename }`` for an
   * upload-triggered run (see {@link runFlowWithFile}).
   */
  trigger_data: Record<string, unknown>;
  /** Accumulated named node outputs at read time. */
  context: Record<string, unknown>;
  error_type?: string | null;
  /** When a ``logic.wait`` node is holding the run (ISO-8601). */
  wait_until?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  approval_notes?: string | null;
  created_at?: string | null;
}

export type SupportCaseStatus = "open" | "waiting_customer" | "resolved";

export interface SupportCaseState {
  status: SupportCaseStatus;
  updated_at: string;
  updated_by: string;
}

/**
 * Reviewer correction sent with {@link approveFlowRun}. Both fields optional
 * and additive over the legacy single-value call (a bare string still works).
 */
export interface ApproveRunEdits {
  /** #930: the SINGLE ``editable_ref`` key the approval node points at. */
  editedValue?: string;
  /**
   * G-2 (Aug 2026): a per-field record patched into the SAME upstream node
   * output — every key is applied, so a reviewer can correct a whole record
   * (e.g. ``{ to, subject, body }``) instead of one string. ``editedFields``
   * wins per overlapping key on the BE.
   */
  editedFields?: Record<string, unknown>;
}

export type ReconciliationDecisionAction =
  | "confirm"
  | "select_candidate"
  | "no_match"
  | "ignore"
  | "update_target"
  | "create_adjustment"
  | "match_invoice"
  | "post_payment"
  | "record_bank_fee"
  | "create_accounting_entry";
export interface ReconciliationDecisionRequest {
  action: ReconciliationDecisionAction;
  idempotency_key: string;
  expected_version: number;
  candidate_event_id?: string;
  reason_code?: string;
  note?: string;
  target_system?: "erp" | "wms" | "accounting";
  connector_mode?: "demo" | "live";
}
export interface ReconciliationDecisionResult {
  status: "succeeded";
  data: {
    decision_id: string;
    resulting_state: string;
    resulting_version: number;
    writeback?: {
      status: string;
      provider: string;
      target_system: string;
      external_reference: string;
      executed_at: string;
    } | null;
  };
  events: string[];
}

// ── Compile / validate ───────────────────────────────────────────────────────
export interface CompileError {
  message: string;
  node_id?: string | null;
}

export interface CompileResult {
  ok: boolean;
  errors: CompileError[];
  estimated_cost_usd?: number | null;
  order: string[];
}

// ── Palette (served from the BE registry SSOT) ───────────────────────────────
export type NodeCategory = "trigger" | "data" | "action" | "logic" | "ai" | "output";
export type NodeModeTier = "essential" | "common" | "advanced";
export type NodeRiskLevel = "low" | "medium" | "high" | "critical";

export interface NodePort {
  key: string;
  label: string;
  type: string;
}

export type ConfigFieldType =
  // ── Legacy built-in flow field types ──────────────────────────────────
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "boolean"
  | "condition"
  | "enterprise_connectors"
  | "doc_templates"
  | "document_picker"
  // ── Rich HITL types (S1: schema_driven nodes now emit the SAME
  //    ``UnifiedFieldSpec`` shape the chat action cards receive). These map
  //    1:1 to the ``@ando/ui`` ``ActionFieldType`` renderers reused by the
  //    NodeInspector, so a Flow node exposes the full connector field set
  //    (account picker, people-autocomplete recipients, attachments,
  //    datetime, richtext, typed strings) rather than a hand-truncated
  //    subset. See docs/Flows-Node-Audit.md §S1. ──────────────────────────
  | "account"
  | "recipients"
  | "file"
  | "datetime"
  | "date"
  | "richtext"
  | "reference"
  | "currency"
  | "email"
  | "phone"
  | "url"
  | "iban"
  | "vat_id"
  | "multiselect"
  | "json"
  | "password";

export interface ConfigFieldSpec {
  key: string;
  label: string;
  type: ConfigFieldType;
  required: boolean;
  placeholder: string;
  options: string[];
  mappable: boolean;
  mode_tier: NodeModeTier;
  /**
   * Helper / description line rendered under the input. Populated from the
   * connector ``UnifiedFieldSpec.help_text`` for schema_driven nodes.
   * Optional — legacy static ``CONFIG_FIELDS`` omit it.
   */
  help_text?: string;
  /**
   * For ``recipients`` fields: the ``key`` of the sibling ``account`` field
   * whose selected connection scopes the people-search autocomplete (same
   * threading the HITL card uses via
   * ``UnifiedFieldSpec.people_search_connection_field_name``). When the
   * referenced account isn't picked yet, the recipients widget degrades to
   * free-text chips.
   */
  people_search_connection_field_name?: string;
  /**
   * For ``account`` fields: the white-label capability
   * (``email`` / ``calendar`` / ``messaging`` / ``crm`` / ``accounting`` /
   * ``pec`` / ``open_banking``) whose connected accounts populate the
   * picker. Mirrors the BE ``_SELECTOR_CONNECTION_TYPES`` capability slugs.
   */
  account_capability?: string;
  /** For ``reference`` fields: the human name of the target object. */
  reference_to?: string;
  /** Inspector v2 presentation metadata. Optional so every v1 node remains valid. */
  section?: "connection" | "operation" | "parameters" | "options" | "settings" | string;
  group?: string;
  order?: number;
  description?: string;
  default?: unknown;
  examples?: unknown[];
  sensitive?: boolean;
  read_only?: boolean;
  multiple?: boolean;
  min?: number;
  max?: number;
  step?: number;
  min_length?: number;
  max_length?: number;
  validation_pattern?: string;
  validation_message?: string;
  visible_when?: Record<string, unknown>;
  required_when?: Record<string, unknown>;
  depends_on?: string[];
  value_modes?: Array<"literal" | "reference" | "expression" | "ai">;
}

/** A data reference bound to a config field via the chip picker. */
export interface DataRef {
  $ref: string;
}

export interface NodePaletteItem {
  node_type: string;
  category: NodeCategory;
  label: string;
  description: string;
  icon: string;
  mode_tier: NodeModeTier;
  risk_level: NodeRiskLevel;
  connector?: string | null;
  action_type?: string | null;
  outputs: NodePort[];
  branches: number;
  schema_driven: boolean;
  config_fields: ConfigFieldSpec[];
  /** Inspector v2 metadata; additive and ignored by legacy clients. */
  definition_version?: number;
  docs_url?: string;
  supports_test?: boolean;
  test_mode?: "read_only" | "dry_run" | "side_effect";
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
}

export interface PaletteResponse {
  nodes: NodePaletteItem[];
}

/**
 * On-demand connector field schema for a single CONNECTOR-BACKED node
 * (Salesforce / Fatture in Cloud / CRM create-update). The palette can't
 * resolve these (no provider/object chosen yet), so they ship with static
 * fields; the inspector fetches the rich set once provider + object are set.
 * ``resolved=false`` (empty fields) ⇒ keep the node's static fields.
 */
export interface NodeSchemaResponse {
  config_fields: ConfigFieldSpec[];
  resolved: boolean;
}

// ── AI co-pilot (text→flow + explain) ────────────────────────────────────────
export interface GenerateFlowResult {
  graph: FlowGraph;
  ok: boolean;
  errors: CompileError[];
}

export interface ExplainResult {
  summary: string;
}

export interface WebhookInfo {
  webhook_url: string;
  webhook_secret: string;
  curl_example: string;
}

/** #1027: free-text -> schedule fields, for the "Attiva" dialog. */
export interface ScheduleInterpretation {
  ok: boolean;
  error?: string | null;
  schedule_type?: "cron" | "interval" | "once" | null;
  cron_expression?: string | null;
  interval_minutes?: number | null;
  run_at?: string | null;
  timezone?: string | null;
  human_readable?: string | null;
  next_run_at?: string | null;
}

// ── Templates + analytics + versions ─────────────────────────────────────────
export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  graph: FlowGraph;
}

export interface FlowStats {
  total_runs: number;
  success_count: number;
  failed_count: number;
  success_rate: number;
  avg_duration_ms?: number | null;
  total_cost_usd: number;
  last_run_at?: string | null;
}

export interface FlowVersionItem {
  version: number;
  label?: string | null;
  created_at?: string | null;
}

export interface FlowVersionsResult {
  versions: FlowVersionItem[];
  current_version?: number | null;
}

/**
 * #930: the upstream node output the paused ``logic.approval`` node's
 * ``editable_ref`` points to, resolved for display so the user can correct
 * it before approving.
 */
export interface EditableApprovalContent {
  node_id: string;
  key: string;
  value: unknown;
}

export interface PendingApproval {
  run_id: string;
  flow_id: string;
  flow_name?: string | null;
  result_summary?: string | null;
  created_at?: string | null;
  editable?: EditableApprovalContent | null;
  /**
   * #1105: flat display rows of the action a HITL gate is withholding — which
   * invoice is about to be transmitted, which query is about to run. Present
   * when a gated ACTION paused the run (no ``logic.approval`` node, so no
   * ``editable``). READ-ONLY: never sent back on approve.
   */
  preview?: Record<string, unknown> | null;
}

export interface FormField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder: string;
  options: string[];
}

export interface FormConfig {
  title: string;
  description: string;
  fields: FormField[];
}

export interface PublicForm {
  flow_id: string;
  title: string;
  description: string;
  fields: FormField[];
}

// ── Fetch functions ──────────────────────────────────────────────────────────
export async function getFlowPalette(): Promise<PaletteResponse> {
  return apiClient.get<PaletteResponse>("/hive-flows/palette");
}

/**
 * Fetch the live connector field schema for a connector-backed node once the
 * user has picked a provider (+ object type / sub-action). Reuses the SAME
 * connector roundtrip as the chat HITL cards, so a flow node renders the
 * identical field set. Degrades to ``{ config_fields: [], resolved: false }``
 * on the BE side (no live connection / bad object) — callers keep static fields.
 */
export async function getNodeConnectorSchema(
  nodeType: string,
  params: { provider?: string; objectType?: string; subAction?: string } = {},
): Promise<NodeSchemaResponse> {
  const qs = new URLSearchParams();
  if (params.provider) qs.set("provider", params.provider);
  if (params.objectType) qs.set("object_type", params.objectType);
  if (params.subAction) qs.set("sub_action", params.subAction);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiClient.get<NodeSchemaResponse>(
    `/hive-flows/nodes/${encodeURIComponent(nodeType)}/schema${suffix}`,
  );
}

/** Describe an automation in plain language → a ready-to-edit graph. */
export async function generateFlow(prompt: string): Promise<GenerateFlowResult> {
  return apiClient.post<GenerateFlowResult>("/hive-flows/generate", { prompt });
}

export interface ExperienceDesignResult {
  ok: boolean;
  summary: string;
  manifest: Record<string, unknown>;
  code_surface?: Record<string, unknown>;
  /** Optional graph proposal. Applied together with the manifest by the editor. */
  graph?: FlowGraph;
  schedule?: ScheduleInterpretation | null;
  readiness?: {
    can_test: boolean;
    missing_configuration: Array<{
      node_id: string;
      node_type: string;
      field: string;
      label: string;
    }>;
  };
  errors: string[];
}

export interface ExperienceDesignAttachment {
  content_id: string;
  filename: string;
  mime_type: string;
  purpose: "reference" | "template" | "test";
}

/** Propose, but never persist, a governed UI-manifest revision from chat. */
export async function designFlowExperience(payload: {
  prompt: string;
  manifest: Record<string, unknown>;
  graph: FlowGraph;
  timezone?: string;
  attachments?: ExperienceDesignAttachment[];
}): Promise<ExperienceDesignResult> {
  return apiClient.post<ExperienceDesignResult>("/hive-flows/experience/design", payload);
}

/** Plain-language Italian summary of what a graph does. */
export async function explainFlow(flowId: string, graph?: FlowGraph): Promise<ExplainResult> {
  return apiClient.post<ExplainResult>(`/hive-flows/${flowId}/explain`, graph ?? null);
}

export async function listFlows(
  args: { page?: number; pageSize?: number; status?: string } = {},
): Promise<FlowListResponse> {
  const params = new URLSearchParams();
  if (args.page) params.set("page", String(args.page));
  if (args.pageSize) params.set("page_size", String(args.pageSize));
  if (args.status) params.set("status", args.status);
  const qs = params.toString();
  return apiClient.get<FlowListResponse>(`/hive-flows${qs ? `?${qs}` : ""}`);
}

export async function getFlow(flowId: string): Promise<Flow> {
  return apiClient.get<Flow>(`/hive-flows/${flowId}`);
}

export async function createFlow(payload: FlowCreatePayload): Promise<Flow> {
  return apiClient.post<Flow>("/hive-flows", payload);
}

export async function updateFlow(flowId: string, payload: FlowUpdatePayload): Promise<Flow> {
  return apiClient.put<Flow>(`/hive-flows/${flowId}`, payload);
}

export async function deleteFlow(flowId: string): Promise<{ ok: boolean }> {
  return apiClient.delete<{ ok: boolean }>(`/hive-flows/${flowId}`);
}

/**
 * Everything shared WITH the caller (issue #1103): org-wide shares plus
 * project-scoped shares from every project the caller belongs to, in one
 * unified list — see ``TeamFlow.project_name`` for how to tell them apart.
 */
export async function listTeamFlows(): Promise<TeamFlowsResponse> {
  return apiClient.get<TeamFlowsResponse>("/hive-flows/team");
}

/** POST ``/hive-flows/{id}/share``: set a flow's team visibility (owner-only). */
export async function shareFlow(flowId: string, visibility: FlowVisibility): Promise<Flow> {
  return apiClient.post<Flow>(`/hive-flows/${flowId}/share`, { visibility });
}

/**
 * Fork a team-shared flow into a new, independently-owned draft. The
 * response's ``reconciliation_nodes`` lists which nodes still point at the
 * original owner's connected account. The caller flags these before the
 * new owner activates the flow.
 */
export async function duplicateFlow(flowId: string): Promise<DuplicateFlowResult> {
  return apiClient.post<DuplicateFlowResult>(`/hive-flows/${flowId}/duplicate`, null);
}

export async function compileFlow(flowId: string, graph?: FlowGraph): Promise<CompileResult> {
  return apiClient.post<CompileResult>(`/hive-flows/${flowId}/compile`, graph ?? null);
}

export async function runFlow(flowId: string, req: FlowRunRequest = {}): Promise<FlowRun> {
  return apiClient.post<FlowRun>(`/hive-flows/${flowId}/run`, req);
}

/**
 * Execute one node plus its upstream dependencies through the canonical flow
 * engine. The server enforces dry-run regardless of caller input, so this
 * endpoint can never be used to perform a write.
 */
export async function testFlowNode(
  flowId: string,
  nodeId: string,
  triggerData: Record<string, unknown> = {},
): Promise<FlowRun> {
  return apiClient.post<FlowRun>(
    `/hive-flows/${encodeURIComponent(flowId)}/nodes/${encodeURIComponent(nodeId)}/test`,
    { dry_run: true, trigger_data: triggerData },
  );
}

/** Export a completed, approved tabular run without creating a second store. */
export async function exportFlowRunXlsx(runId: string): Promise<Blob> {
  return apiClient.requestBlob(`/hive-flows/runs/${encodeURIComponent(runId)}/export.xlsx`);
}

export type CommissionStatementStatus =
  | "draft"
  | "review"
  | "approved"
  | "invoice_requested"
  | "invoiced"
  | "paid"
  | "excluded";
export interface CommissionStatement {
  statement_id: string;
  seller_key: string;
  seller_name?: string | null;
  seller_email?: string | null;
  period_start: string;
  period_end: string;
  status: CommissionStatementStatus;
  row_version: number;
  currency?: string;
  order_count?: number;
  line_count?: number;
  total_accepted?: number;
  total_delta?: number;
  total_commission?: number;
  blocking_line_count?: number;
  [key: string]: unknown;
}
export interface CommissionStatementLine {
  line_item_id: string;
  document_number?: string;
  product_code?: string;
  product_card_unit_price?: number;
  accepted_offer_unit_price?: number;
  delta_total?: number;
  commission_amount?: number;
  [key: string]: unknown;
}
export interface CommissionStatementDetail {
  statement: CommissionStatement;
  lines: CommissionStatementLine[];
  events: Array<Record<string, unknown>>;
  delivery?: Record<string, unknown> | null;
}
export interface CommissionStatementsResponse {
  statements: CommissionStatement[];
}
export interface CommissionLegacyImportResult {
  file_name: string;
  file_sha256: string;
  row_count: number;
  imported_count: number;
  replayed_count: number;
  counts: Record<string, number>;
}
export interface CommissionStatementTransition {
  target_status: CommissionStatementStatus;
  expected_version: number;
  idempotency_key: string;
  reason?: string;
  invoice_number?: string;
  invoice_date?: string;
  payment_reference?: string;
}
export async function listCommissionStatements(
  params: {
    periodStart?: string;
    periodEnd?: string;
    sellerKey?: string;
    status?: CommissionStatementStatus;
  } = {},
): Promise<CommissionStatementsResponse> {
  const query = new URLSearchParams();
  if (params.periodStart) query.set("period_start", params.periodStart);
  if (params.periodEnd) query.set("period_end", params.periodEnd);
  if (params.sellerKey) query.set("seller_key", params.sellerKey);
  if (params.status) query.set("status", params.status);
  return apiClient.get<CommissionStatementsResponse>(
    `/hive-flows/commission-statements${query.size ? `?${query}` : ""}`,
  );
}
export async function getCommissionStatement(
  statementId: string,
): Promise<CommissionStatementDetail> {
  return apiClient.get<CommissionStatementDetail>(
    `/hive-flows/commission-statements/${encodeURIComponent(statementId)}`,
  );
}
export async function createCommissionStatementsFromRun(
  runId: string,
  payload: {
    period_start: string;
    period_end: string;
    calculation_version: string;
    source_connection_key?: string;
    source_company?: string;
    currency?: string;
  },
): Promise<CommissionStatementsResponse> {
  return apiClient.post<CommissionStatementsResponse>(
    `/hive-flows/commission-statements/from-run/${encodeURIComponent(runId)}`,
    payload,
  );
}
export async function importCommissionLegacyHistory(
  runId: string,
  file: File,
): Promise<CommissionLegacyImportResult> {
  const form = new FormData();
  form.append("file", file);
  return apiClient.postMultipart<CommissionLegacyImportResult>(
    `/hive-flows/commission-statements/legacy-import/${encodeURIComponent(runId)}`,
    form,
  );
}
export async function transitionCommissionStatement(
  statementId: string,
  payload: CommissionStatementTransition,
): Promise<{ statement: CommissionStatement }> {
  return apiClient.post<{ statement: CommissionStatement }>(
    `/hive-flows/commission-statements/${encodeURIComponent(statementId)}/transitions`,
    payload,
  );
}
export async function requestCommissionInvoice(
  statementId: string,
  payload: { expected_version: number; idempotency_key: string; recipient?: string },
): Promise<{ statement: CommissionStatement; delivery?: Record<string, unknown> | null }> {
  return apiClient.post<{
    statement: CommissionStatement;
    delivery?: Record<string, unknown> | null;
  }>(
    `/hive-flows/commission-statements/${encodeURIComponent(statementId)}/request-invoice`,
    payload,
  );
}
export interface CommissionInvoiceSendInput {
  expected_version: number;
  idempotency_key: string;
  recipient: string;
  provider: "gmail" | "outlook";
  from_account?: string;
  subject: string;
  body_text: string;
}
export async function sendCommissionInvoice(
  statementId: string,
  payload: CommissionInvoiceSendInput,
): Promise<{
  statement: CommissionStatement;
  delivery?: Record<string, unknown> | null;
  message_id?: string;
}> {
  return apiClient.post(
    `/hive-flows/commission-statements/${encodeURIComponent(statementId)}/send-invoice`,
    payload,
  );
}
export async function exportCommissionStatementXlsx(statementId: string): Promise<Blob> {
  return apiClient.requestBlob(
    `/hive-flows/commission-statements/${encodeURIComponent(statementId)}/export.xlsx`,
  );
}

/**
 * Run a flow by uploading a document (PDF) as the trigger. BE:
 * ``POST /hive-flows/{flow_id}/run-upload`` (multipart) — the file is persisted
 * as a ``content_item`` and its id lands in the run's ``trigger_data`` as
 * ``{ content_item_id, filename }`` (nodes ref it via
 * ``{"$ref": "trigger.content_item_id"}``). Separate from {@link runFlow}
 * (which stays JSON): FastAPI can't mix a multipart handler with a JSON body.
 *
 * Multipart via ``postMultipart`` (NOT ``post``) so ``fetch`` sets the
 * ``boundary`` FastAPI's ``UploadFile`` parser needs — mirrors
 * ``searchSimilarCadByFile`` / ``uploadDfmChecklist``.
 */
/**
 * ONE encoding of the run-upload multipart contract (product AND console
 * transports): a single ``file`` keeps the legacy singular seed; an array goes
 * as ``files[]`` → ``<trigger>.content_item_ids`` / ``.filenames`` (the first
 * also fills the singular ports).
 */
export function appendRunUploadFiles(form: FormData, file: File | readonly File[]): FormData {
  if (Array.isArray(file)) {
    for (const item of file as readonly File[]) form.append("files", item, item.name);
  } else {
    const single = file as File;
    form.append("file", single, single.name);
  }
  return form;
}

export async function runFlowWithFile(
  flowId: string,
  file: File | readonly File[],
  opts: { dryRun?: boolean; triggerData?: Record<string, unknown> } = {},
): Promise<FlowRun> {
  const form = appendRunUploadFiles(new FormData(), file);
  if (opts.dryRun !== undefined) form.append("dry_run", String(opts.dryRun));
  if (opts.triggerData) form.append("trigger_data", JSON.stringify(opts.triggerData));
  // The request persists every attachment before the asynchronous run exists.
  // Large monthly batches can exceed the API client's 120 s default even with
  // bounded concurrent persistence on the server.
  const timeout = Array.isArray(file) && file.length > 1 ? 15 * 60_000 : undefined;
  return apiClient.postMultipart<FlowRun>(`/hive-flows/${flowId}/run-upload`, form, { timeout });
}

/**
 * Full projection of one run — poll a long AI pipeline and re-hydrate the run
 * surface after a reload. BE: ``GET /hive-flows/runs/{run_id}``. Tenant/RLS
 * scoped (another user's run 404s). Two path segments, so it never collides
 * with ``GET /hive-flows/{flow_id}`` (one segment).
 */
export async function getFlowRun(runId: string): Promise<FlowRunDetailResponse> {
  return apiClient.get<FlowRunDetailResponse>(`/hive-flows/runs/${runId}`);
}

export async function setFlowSupportCaseStatus(
  runId: string,
  status: SupportCaseStatus,
): Promise<SupportCaseState> {
  return apiClient.patch<SupportCaseState>(`/hive-flows/runs/${runId}/support-case`, { status });
}

export interface SupportCaseReplyResult {
  message_id: string;
  provider: string;
  to: string;
  body: string;
  sent_at: string;
  sent_by: string;
}

export async function sendFlowSupportCaseReply(
  runId: string,
  body: string,
): Promise<SupportCaseReplyResult> {
  return apiClient.post<SupportCaseReplyResult>(`/hive-flows/runs/${runId}/support-case/reply`, {
    body,
  });
}

/**
 * Run history for a flow (most recent first, dry-runs excluded) — powers the
 * operator run-surface's history rail so a settled result can be reopened.
 * BE: ``GET /hive-flows/{flow_id}/runs`` → {@link FlowRunListResponse}.
 */
export async function listFlowRuns(
  flowId: string,
  opts: { limit?: number } = {},
): Promise<FlowRunListResponse> {
  const qs = opts.limit ? `?limit=${opts.limit}` : "";
  return apiClient.get<FlowRunListResponse>(`/hive-flows/${flowId}/runs${qs}`);
}

/**
 * Approve a run paused at a ``logic.approval`` node → resumes execution.
 *
 * Optional reviewer correction, patched into the pending approval's editable
 * upstream node output BEFORE the run resumes, so downstream nodes (e.g. "Invia
 * email") see the fix. Two additive, back-compatible shapes:
 *   • ``editedValue`` (#930) — the SINGLE ``editable_ref`` key. Passing a bare
 *     string keeps the legacy call site (``approveFlowRun(runId, value)``).
 *   • ``editedFields`` (G-2) — a per-field record; every key is patched into
 *     that SAME node output, so a whole record ({to, subject, body}) can be
 *     corrected. ``editedFields`` wins per overlapping key (BE-side).
 */
export async function approveFlowRun(
  runId: string,
  edits?: string | ApproveRunEdits,
): Promise<{ ok: boolean; status: string }> {
  const normalized: ApproveRunEdits =
    typeof edits === "string" ? { editedValue: edits } : (edits ?? {});
  return apiClient.post<{ ok: boolean; status: string }>(`/hive-flows/runs/${runId}/approve`, {
    edited_value: normalized.editedValue ?? null,
    edited_fields: normalized.editedFields ?? null,
  });
}

/**
 * Reject a run paused at a ``logic.approval`` node — terminal: the run never
 * resumes and downstream writes never happen. BE:
 * ``POST /hive-flows/runs/{run_id}/reject`` → ``{ ok: true, status: "rejected" }``.
 * ``reason`` lands in the run's ``approval_notes`` for the audit trail.
 */
export async function rejectFlowRun(
  runId: string,
  reason?: string,
): Promise<{ ok: boolean; status: string }> {
  return apiClient.post<{ ok: boolean; status: string }>(`/hive-flows/runs/${runId}/reject`, {
    reason: reason ?? null,
  });
}

export async function decideReconciliationItem(
  runId: string,
  itemId: string,
  decision: ReconciliationDecisionRequest,
): Promise<ReconciliationDecisionResult> {
  return apiClient.post<ReconciliationDecisionResult>(
    `/hive-flows/runs/${runId}/reconciliation-items/${itemId}/decision`,
    decision,
  );
}

/**
 * #1027: turn free text ("ogni lunedi alle 9") into schedule fields.
 * Cherry-picked from Gian's #46 (converge into frd) — the FE input on
 * the ScheduleEditor sends the raw string here, the BE returns the
 * parsed cron / interval / one-shot fields that the ScheduleEditor
 * then patches into the flow's schedule config.
 */
export async function interpretFlowSchedule(
  text: string,
  timezone?: string,
): Promise<ScheduleInterpretation> {
  return apiClient.post<ScheduleInterpretation>("/hive-flows/schedule/interpret", {
    text,
    timezone: timezone ?? null,
  });
}

/** Runs paused awaiting the user's approval. */
export async function getPendingApprovals(): Promise<{ approvals: PendingApproval[] }> {
  return apiClient.get<{ approvals: PendingApproval[] }>("/hive-flows/pending-approvals");
}

/** Publish a flow as a public fillable form. */
export async function publishFlowForm(flowId: string, config: FormConfig): Promise<Flow> {
  return apiClient.post<Flow>(`/hive-flows/${flowId}/publish-form`, { config });
}

/** Public form schema (no auth required server-side). */
export async function getPublicForm(flowId: string): Promise<PublicForm> {
  return apiClient.get<PublicForm>(`/hive-flows/forms/${flowId}`);
}

/** Submit a public form → starts the flow with the values. */
export async function submitPublicForm(
  flowId: string,
  values: Record<string, unknown>,
): Promise<{ ok: boolean; run_id: string }> {
  return apiClient.post<{ ok: boolean; run_id: string }>(
    `/hive-flows/forms/${flowId}/submit`,
    values,
  );
}

/** URL + secret + curl for a webhook-triggered flow (after activation). */
export async function getFlowWebhookInfo(flowId: string): Promise<WebhookInfo> {
  return apiClient.get<WebhookInfo>(`/hive-flows/${flowId}/webhook-url`);
}

/** Built-in starter templates for the hub gallery. */
export async function getFlowTemplates(): Promise<{ templates: FlowTemplate[] }> {
  return apiClient.get<{ templates: FlowTemplate[] }>("/hive-flows/templates");
}

/** Per-flow run analytics. */
export async function getFlowStats(flowId: string): Promise<FlowStats> {
  return apiClient.get<FlowStats>(`/hive-flows/${flowId}/stats`);
}

/** Graph version history. */
export async function getFlowVersions(flowId: string): Promise<FlowVersionsResult> {
  return apiClient.get<FlowVersionsResult>(`/hive-flows/${flowId}/versions`);
}

/** Restore a prior graph version (creates a new version). */
export async function restoreFlowVersion(flowId: string, version: number): Promise<Flow> {
  return apiClient.post<Flow>(`/hive-flows/${flowId}/versions/${version}/restore`, null);
}

// ── Banco room: real-time collaboration on a paused run (presence + locks) ────
// COMMANDS over REST (below); EVENTS ride the existing per-user WebSocket
// (``banco_presence`` / ``banco_lock`` / ``banco_unlock``).
// BE: ``src/services/hive_flows/banco_room.py`` + ``/hive-flows/runs/{id}/room/*``.
export interface BancoUser {
  user_id: string;
  name: string;
  initials: string;
  color: string;
}

export interface BancoLock {
  field: string;
  user_id: string;
  name: string;
  initials: string;
  color: string;
}

export interface BancoRoomSnapshot {
  run_id: string;
  users: BancoUser[];
  locks: BancoLock[];
}

export interface BancoLockResult {
  ok: boolean;
  holder: BancoUser | null;
}

/** Enter the room → live snapshot (present users + held locks). */
export async function bancoJoin(runId: string): Promise<BancoRoomSnapshot> {
  return apiClient.post<BancoRoomSnapshot>(`/hive-flows/runs/${runId}/room/join`, null);
}

/** Leave the room (releases this user's locks). Best-effort. */
export async function bancoLeave(runId: string): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(`/hive-flows/runs/${runId}/room/leave`, null);
}

/** Presence keep-alive; ``fields`` = the locks this client still holds. */
export async function bancoHeartbeat(runId: string, fields: string[]): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(`/hive-flows/runs/${runId}/room/heartbeat`, { fields });
}

/** Take the soft field-lock. ``ok=false`` → ``holder`` already owns it. */
export async function bancoLock(runId: string, field: string): Promise<BancoLockResult> {
  return apiClient.post<BancoLockResult>(`/hive-flows/runs/${runId}/room/lock`, { field });
}

/** Release the field-lock (only if this user owns it). */
export async function bancoUnlock(runId: string, field: string): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(`/hive-flows/runs/${runId}/room/unlock`, { field });
}
